import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Save } from "lucide-react";
import {
  fetchPayPeriods,
  fetchEmployees,
  fetchSquareTipShare,
  fetchSquareTipShareEntries,
  upsertSquareTipShare,
  upsertSquareTipShareEntriesBatch,
} from "../tipsApi";
import { calculateSquareTipShare } from "../tipCalculator";
import type { PayPeriod, Employee, SquareTipShareEntry } from "../types";
import { employeeDisplayName, employeesForPeriod, uniqueById } from "../employeePeriod";

function fmtRange(pp: PayPeriod): string {
  const s = new Date(pp.start_date + "T00:00:00");
  const e = new Date(pp.end_date + "T00:00:00");
  const fmt = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${fmt(s)} – ${fmt(e)}, ${e.getFullYear()}`;
}

function money(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

const DEFAULT_FEE_PCT = 3.0;

export function SquareTipShareView() {
  const [periods, setPeriods] = useState<PayPeriod[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [savedEntries, setSavedEntries] = useState<SquareTipShareEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [totalTips, setTotalTips] = useState("");
  const [feePct, setFeePct] = useState(DEFAULT_FEE_PCT);
  const [hours, setHours] = useState<Record<string, string>>({});
  const [extraTips, setExtraTips] = useState<Record<string, string>>({});
  const [eventTips, setEventTips] = useState<Record<string, string[]>>({});

  const selected = useMemo(
    () => periods.find((p) => p.id === selectedId) ?? null,
    [periods, selectedId],
  );

  const employees = useMemo(
    () => employeesForPeriod(allEmployees, selected, savedEntries.map((e) => e.employee_id)),
    [allEmployees, selected, savedEntries],
  );

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const [pp, fdEmp, kitEmp, coachEmp] = await Promise.all([
        fetchPayPeriods(),
        fetchEmployees("front_desk"),
        fetchEmployees("kitchen"),
        fetchEmployees("axe_coach"),
      ]);
      setPeriods(pp);
      setAllEmployees(uniqueById([...fdEmp, ...kitEmp, ...coachEmp]));
      if (pp.length > 0 && !selectedId) setSelectedId(pp[0].id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);

  const loadPeriodData = useCallback(async () => {
    if (!selected) return;
    setError(null);
    setSuccessMsg(null);
    setSavedEntries([]);
    try {
      const [share, entries] = await Promise.all([
        fetchSquareTipShare(selected.id),
        fetchSquareTipShareEntries(selected.id),
      ]);

      setTotalTips(share ? String(share.total_tips) : "");
      setFeePct(share ? Number(share.processing_fee_pct) : DEFAULT_FEE_PCT);
      setSavedEntries(entries);

      const roster = employeesForPeriod(
        allEmployees,
        selected,
        entries.map((e) => e.employee_id),
      );
      const entryMap = new Map<string, SquareTipShareEntry>();
      for (const e of entries) entryMap.set(e.employee_id, e);

      const h: Record<string, string> = {};
      const ex: Record<string, string> = {};
      const ev: Record<string, string[]> = {};
      for (const emp of roster) {
        const entry = entryMap.get(emp.id);
        h[emp.id] = entry ? String(entry.hours_worked) : "";
        ex[emp.id] = entry && Number(entry.extra_tips) > 0 ? String(entry.extra_tips) : "";
        const detail = entry?.event_tips_detail ?? [];
        ev[emp.id] = detail.length > 0 ? detail.map(String) : [""];
      }
      setHours(h);
      setExtraTips(ex);
      setEventTips(ev);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [selected, allEmployees]);

  useEffect(() => { loadPeriodData(); }, [loadPeriodData]);

  const calculation = useMemo(() => {
    const total = parseFloat(totalTips) || 0;

    const empInputs = employees.map((e) => ({
      employee_id: e.id,
      employee_name: e.name,
      hours_worked: parseFloat(hours[e.id] || "0") || 0,
      extra_tips: parseFloat(extraTips[e.id] || "0") || 0,
      event_tips: (eventTips[e.id] ?? [""]).map((v) => parseFloat(v) || 0).filter((v) => v > 0),
    }));

    return calculateSquareTipShare(total, feePct, empInputs);
  }, [totalTips, feePct, employees, hours, extraTips, eventTips]);

  const hasAnyData = useMemo(() => {
    const total = parseFloat(totalTips) || 0;
    if (total > 0) return true;
    for (const emp of employees) {
      if ((parseFloat(hours[emp.id] || "0") || 0) > 0) return true;
      if ((parseFloat(extraTips[emp.id] || "0") || 0) > 0) return true;
      if ((eventTips[emp.id] ?? []).some((v) => (parseFloat(v) || 0) > 0)) return true;
    }
    return false;
  }, [totalTips, employees, hours, extraTips, eventTips]);

  async function handleSave() {
    if (!selected) return;
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await upsertSquareTipShare({
        pay_period_id: selected.id,
        total_tips: calculation.total_tips,
        processing_fee_pct: feePct,
      });

      const rows = calculation.entries.map((e) => {
        const detail = (eventTips[e.employee_id] ?? [""]).map((v) => parseFloat(v) || 0).filter((v) => v > 0);
        return {
          pay_period_id: selected.id,
          employee_id: e.employee_id,
          hours_worked: e.hours_worked,
          calculated_share: e.calculated_share,
          extra_tips: e.extra_tips,
          event_tips_detail: detail,
        };
      });

      await upsertSquareTipShareEntriesBatch(rows);
      setSuccessMsg("Saved successfully.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="p-6 text-sm text-tribal-muted">Loading…</div>;

  if (periods.length === 0) {
    return (
      <div className="p-6 text-sm text-tribal-muted">
        No pay periods defined yet. Go to the <strong>Pay Periods</strong> tab to create one.
      </div>
    );
  }

  const locked = selected?.is_locked ?? false;

  return (
    <div className="mx-auto max-w-5xl p-6">
      {error && <div className="mb-4 rounded-lg bg-red-900/20 px-3 py-2 text-sm text-red-400">{error}</div>}
      {successMsg && <div className="mb-4 rounded-lg bg-emerald-900/20 px-3 py-2 text-sm text-emerald-400">{successMsg}</div>}

      {/* Period selector */}
      <div className="mb-6 flex items-center gap-3">
        <label className="text-sm text-tribal-muted">Pay Period</label>
        <select
          value={selectedId ?? ""}
          onChange={(e) => setSelectedId(e.target.value)}
          className="rounded-md border border-tribal-border bg-tribal-bg px-3 py-1.5 text-sm focus:border-tribal-red focus:outline-none"
        >
          {periods.map((pp) => (
            <option key={pp.id} value={pp.id}>
              {fmtRange(pp)}{pp.is_locked ? " (locked)" : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_18rem]">
        {/* Entry form */}
        <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          {/* Total Square Tips input */}
          <div className="mb-5">
            <label className="mb-1 block text-xs font-medium text-tribal-muted">
              Total Square Tips (for this pay period)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={totalTips}
              onChange={(e) => setTotalTips(e.target.value)}
              disabled={locked}
              placeholder="0.00"
              className="w-48 rounded-md border border-tribal-border bg-tribal-bg px-3 py-1.5 text-sm tabular-nums focus:border-tribal-red focus:outline-none disabled:opacity-50"
            />
            {calculation.total_tips > 0 && (
              <div className="mt-2 flex gap-4 text-xs text-tribal-muted">
                <span>
                  3% fee: <span className="tabular-nums text-red-400">−{money(calculation.processing_fee)}</span>
                </span>
                <span>
                  Net pool: <span className="tabular-nums font-medium text-white">{money(calculation.net_pool)}</span>
                </span>
                {calculation.total_hours > 0 && (
                  <span>
                    Rate: <span className="tabular-nums text-white">{money(calculation.tip_per_hour)}/hr</span>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Employee table */}
          {employees.length === 0 ? (
            <p className="text-xs text-tribal-muted">
              No active Front Desk, Kitchen, or Axe Coach employees. Add them in the <strong>Employees</strong> tab.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-tribal-border text-left text-tribal-muted">
                    <th className="pb-2 pr-3 font-medium">Employee</th>
                    <th className="pb-2 pr-3 font-medium">Role</th>
                    <th className="pb-2 pr-3 font-medium text-right">Hours</th>
                    <th className="pb-2 pr-3 font-medium text-right">Tip Share</th>
                    <th className="pb-2 pr-3 font-medium text-right">Extra Tips</th>
                    <th className="pb-2 pr-3 font-medium text-right">Event Tips</th>
                    <th className="pb-2 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => {
                    const calc = calculation.entries.find((e) => e.employee_id === emp.id);
                    const hasHours = (parseFloat(hours[emp.id] || "0") || 0) > 0;
                    return (
                      <tr key={emp.id} className="border-b border-tribal-border/50 last:border-0">
                        <td className="py-2 pr-3 font-medium">{employeeDisplayName(emp)}</td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-1">
                            {emp.roles.includes("front_desk") && (
                              <span className="rounded-full px-2 py-0.5 text-[10px] bg-purple-500/15 text-purple-300">FD</span>
                            )}
                            {emp.roles.includes("kitchen") && (
                              <span className="rounded-full px-2 py-0.5 text-[10px] bg-emerald-500/15 text-emerald-300">Kit</span>
                            )}
                            {emp.roles.includes("axe_coach") && (
                              <span className="rounded-full px-2 py-0.5 text-[10px] bg-amber-500/15 text-amber-300">AC</span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={hours[emp.id] ?? ""}
                            onChange={(e) => setHours((prev) => ({ ...prev, [emp.id]: e.target.value }))}
                            disabled={locked}
                            placeholder="0"
                            className="w-20 rounded border border-tribal-border bg-tribal-bg px-2 py-1 text-right tabular-nums disabled:opacity-50"
                          />
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {hasHours && calc ? money(calc.calculated_share) : "—"}
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={extraTips[emp.id] ?? ""}
                            onChange={(e) => setExtraTips((prev) => ({ ...prev, [emp.id]: e.target.value }))}
                            disabled={locked}
                            placeholder="0"
                            className="w-24 rounded border border-tribal-border bg-tribal-bg px-2 py-1 text-right tabular-nums disabled:opacity-50"
                          />
                        </td>
                        <td className="py-2 pr-3 align-top">
                          {(() => {
                            const tips = eventTips[emp.id] ?? [""];
                            const lastVal = parseFloat(tips[tips.length - 1] || "0") || 0;
                            const eventTotal = tips.reduce((s, v) => s + (parseFloat(v) || 0), 0);
                            return (
                              <div className="space-y-1">
                                {tips.map((val, idx) => (
                                  <input
                                    key={idx}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={val}
                                    onChange={(e) =>
                                      setEventTips((prev) => {
                                        const arr = [...(prev[emp.id] ?? [""])];
                                        arr[idx] = e.target.value;
                                        return { ...prev, [emp.id]: arr };
                                      })
                                    }
                                    disabled={locked}
                                    placeholder="0"
                                    className="w-24 rounded border border-tribal-border bg-tribal-bg px-2 py-1 text-right tabular-nums disabled:opacity-50"
                                  />
                                ))}
                                {!locked && lastVal > 0 && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setEventTips((prev) => ({
                                        ...prev,
                                        [emp.id]: [...(prev[emp.id] ?? [""]), ""],
                                      }))
                                    }
                                    className="flex items-center gap-0.5 text-[10px] text-tribal-muted hover:text-white"
                                  >
                                    <Plus className="h-3 w-3" /> Add Event
                                  </button>
                                )}
                                {tips.length > 1 && eventTotal > 0 && (
                                  <div className="text-right tabular-nums text-[10px] font-medium text-amber-300">
                                    Total: {money(eventTotal)}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="py-2 text-right tabular-nums font-medium">
                          {calc && calc.total > 0 ? money(calc.total) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {calculation.total_hours > 0 && (
                  <tfoot>
                    <tr className="border-t border-tribal-border text-tribal-muted">
                      <td className="pt-2 pr-3 font-medium">Totals</td>
                      <td className="pt-2 pr-3" />
                      <td className="pt-2 pr-3 text-right tabular-nums">{calculation.total_hours.toFixed(2)}</td>
                      <td className="pt-2 pr-3 text-right tabular-nums">{money(calculation.net_pool)}</td>
                      <td className="pt-2 pr-3 text-right tabular-nums">
                        {money(calculation.entries.reduce((s, e) => s + e.extra_tips, 0))}
                      </td>
                      <td className="pt-2 pr-3 text-right tabular-nums">
                        {money(calculation.entries.reduce((s, e) => s + e.event_tips_total, 0))}
                      </td>
                      <td className="pt-2 text-right tabular-nums font-medium text-white">
                        {money(calculation.entries.reduce((s, e) => s + e.total, 0))}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* Save button */}
          {!locked && (
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !hasAnyData}
                className="flex items-center gap-1.5 rounded-md bg-tribal-red px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-tribal-red/80 disabled:opacity-50"
              >
                <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          )}
        </div>

        {/* Summary sidebar */}
        <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <h3 className="mb-3 text-sm font-semibold">Period Summary</h3>
          {!hasAnyData ? (
            <p className="text-xs text-tribal-muted">Enter data to see the breakdown.</p>
          ) : (
            <div className="space-y-2 text-xs">
              {calculation.total_tips > 0 && (
                <>
                  <div className="flex justify-between text-tribal-muted">
                    <span>Total Tips</span>
                    <span className="tabular-nums">{money(calculation.total_tips)}</span>
                  </div>
                  <div className="flex justify-between text-tribal-muted">
                    <span>3% Fee</span>
                    <span className="tabular-nums text-red-400">−{money(calculation.processing_fee)}</span>
                  </div>
                  <div className="flex justify-between border-b border-tribal-border pb-2 font-medium">
                    <span>Net Pool</span>
                    <span className="tabular-nums">{money(calculation.net_pool)}</span>
                  </div>
                </>
              )}
              {calculation.total_hours > 0 && (
                <>
                  <div className="flex justify-between text-tribal-muted">
                    <span>Total Hours</span>
                    <span className="tabular-nums">{calculation.total_hours.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-b border-tribal-border pb-2 text-tribal-muted">
                    <span>Tip / Hour</span>
                    <span className="tabular-nums">{money(calculation.tip_per_hour)}</span>
                  </div>
                </>
              )}

              {calculation.entries
                .filter((e) => e.total > 0)
                .map((e) => (
                  <div key={e.employee_id} className="rounded-md bg-white/[0.03] px-3 py-2">
                    <div className="font-medium">{e.employee_name}</div>
                    <div className="mt-1 flex justify-between text-tribal-muted">
                      <span>{e.hours_worked.toFixed(2)} hrs ({(e.percent_of_hours * 100).toFixed(1)}%)</span>
                      <span className="tabular-nums">{money(e.calculated_share)}</span>
                    </div>
                    {e.extra_tips > 0 && (
                      <div className="flex justify-between text-tribal-muted">
                        <span>Extra tips</span>
                        <span className="tabular-nums text-amber-300">+{money(e.extra_tips)}</span>
                      </div>
                    )}
                    {e.event_tips_total > 0 && (
                      <div className="flex justify-between text-tribal-muted">
                        <span>Event tips</span>
                        <span className="tabular-nums text-amber-300">+{money(e.event_tips_total)}</span>
                      </div>
                    )}
                    <div className="mt-1 flex justify-between border-t border-tribal-border pt-1 font-medium">
                      <span>Total</span>
                      <span className="tabular-nums">{money(e.total)}</span>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
